import axios, { AxiosResponse } from 'axios';
import { ConsoleFacade } from '../helpers';
import { AgendaTemplateClient, LogClient } from './clients';
import { ActivityClient } from '@/shared/api-client/clients';
import { ActivityEventClient } from '@/shared/api-client/clients';
import { AgendaClient } from '@/shared/api-client/clients';
import { AttendanceClient } from '@/shared/api-client/clients';
import { CandidatureClient } from '@/shared/api-client/clients';
import { EditorialListClient } from '@/shared/api-client/clients';
import { ElectionClient } from '@/shared/api-client/clients';
import { ElectionEncryptionClient } from '@/shared/api-client/clients';
import { ElectionResultClient } from '@/shared/api-client/clients';
import { FileBucketClient } from '@/shared/api-client/clients';
import { ImpersonationClient } from '@/shared/api-client/clients';
import { NominationClient } from '@/shared/api-client/clients';
import { SendOutsClient } from '@/shared/api-client/clients';
import { UserClient } from '@/shared/api-client/clients';
import { UserFileImportProcessClient } from '@/shared/api-client/clients';
import { VoteClient } from '@/shared/api-client/clients';
import { VoterClient } from '@/shared/api-client/clients';
import { MotionClient } from '@/shared/api-client/clients';
import { UserTaskClient } from './clients/user-task-client';
import { MotionCategoryClient } from '@/shared/api-client/clients/motion-category-client';
import { SpeakerRequestClient } from '@/shared/api-client/clients/speaker-request-client';

export class ApiClient {

	public baseUrl = '/easyvote-api';

	private axiosClient = axios.create({
		baseURL: this.baseUrl
	});

	private preventInternetExplorerCache() {

		this.axiosClient.interceptors.request.use((config) => {

			if (config.url && config.method === 'get' || config.method === 'GET') {

				const random = Math.ceil(Math.random() * 99999); // So IE won't cache requests

				const separator = (config.url || '').indexOf('?') === -1 ? '?' : '&';

				config.url += `${separator}r=${random}`;
			}

			return config;

		}, (error) => {

			return Promise.reject(error);
		});
	}

	private applyUnauthorizedErrorHandler(): void {

		this.errorHandlers.push((error) => {

			if (error.status === 401) {

				ConsoleFacade.log(`Error handled by handler 'addErrorHandlerForUnauthorizedAndForbidden' for 401 Unauthorized`);

				window.location.reload();
			}

			if (error.status === 403) {

				ConsoleFacade.log(`Error handled by handler 'addErrorHandlerForUnauthorizedAndForbidden' for 403 Forbidden`);

				window.location.reload();
			}
		});
	}

	public successHandlers = new Array<(response: AxiosResponse<any>) => void>();

	public errorHandlers = new Array<(response: AxiosResponse<any>) => void>();

	public editorialList: EditorialListClient = new EditorialListClient(this.axiosClient);

	public attendance: AttendanceClient = new AttendanceClient(this.axiosClient);

	public user: UserClient = new UserClient(this.axiosClient);

	public vote: VoteClient = new VoteClient(this.axiosClient);

	public voter: VoterClient = new VoterClient(this.axiosClient);

	public election: ElectionClient = new ElectionClient(this.axiosClient);

	public electionResult: ElectionResultClient = new ElectionResultClient(this.axiosClient, this.baseUrl);

	public electionEncryption: ElectionEncryptionClient = new ElectionEncryptionClient(this.axiosClient);

	public candidature: CandidatureClient = new CandidatureClient(this.axiosClient);

	public nomination: NominationClient = new NominationClient(this.axiosClient, this.baseUrl);

	public sendOuts: SendOutsClient = new SendOutsClient(this.axiosClient);

	public impersonation: ImpersonationClient = new ImpersonationClient(this.axiosClient);

	public fileBucket: FileBucketClient = new FileBucketClient(this.axiosClient);

	public userFileImportProcess: UserFileImportProcessClient = new UserFileImportProcessClient(this.axiosClient);

	public activity: ActivityClient = new ActivityClient(this.axiosClient, this.baseUrl);

	public activityAgenda: AgendaClient = new AgendaClient(this.axiosClient);

	public activityAgendaTemplate: AgendaTemplateClient = new AgendaTemplateClient(this.axiosClient);

	public activityEvent: ActivityEventClient = new ActivityEventClient(this.axiosClient);

	public motion: MotionClient = new MotionClient(this.axiosClient);

	public motionCategory: MotionCategoryClient = new MotionCategoryClient(this.axiosClient);

	public log: LogClient = new LogClient(this.axiosClient);

	public userTask: UserTaskClient = new UserTaskClient(this.axiosClient);

	public speakerRequest: SpeakerRequestClient = new SpeakerRequestClient(this.axiosClient);

	constructor(customErrorHandler?: (response: AxiosResponse<any>) => void) {

		this.preventInternetExplorerCache();

		if (customErrorHandler) {

			this.errorHandlers = [];
			this.errorHandlers.push(customErrorHandler);

		} else {

			this.applyUnauthorizedErrorHandler();
		}

		this.axiosClient.interceptors.response.use((response) => {

			this.successHandlers.forEach(handler => {

				handler(response);
			});

			return response;

		}, (error) => {

			this.errorHandlers.forEach(handler => {

				handler(error.response);
			});

			return Promise.reject(error);
		});
	}
}
